import { Connection } from '@/common/models/Connection';
import { NetworkDataType } from '@/common/models/NetworkDataTransfers';
import { ControllerUserDto } from '@/controller/models/ControllerUserModel';
import { Subscription, PlayerDto } from '@toilet-paper-games/core';
import {
  IReactionOptions,
  IReactionPublic,
  makeAutoObservable,
  reaction,
  runInAction,
} from 'mobx';
import { UsersManager } from './UsersManager';

export const INACTIVE_ALLOWANCE = 10_000;

export class HosterUserModel {
  connection: Connection | null = null;
  screenName: string | null = null;
  image: string | null = null;
  isHost = false;
  subscription = Subscription.Free;

  active = false;
  timeWentInactive: number | null = null;

  networkReady = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private kickTimeout: NodeJS.Timeout | null = null;
  destructors: (() => void)[] = [];

  reconnect() {
    this.active = false;
    this.timeWentInactive = null;
    this.networkReady = false;

    this.setupReactions();
  }

  constructor(private usersManager: UsersManager, public id: string) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.setupReactions();
  }

  destroy() {
    this.destructors.forEach((destructor) => destructor());
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.kickTimeout) clearTimeout(this.kickTimeout);
  }

  kick(reason: 'inactivity' | 'manual') {
    if (reason !== 'inactivity') {
      this.usersManager.kickedUsers.set(this.id, this);
      this.usersManager.usersMap.delete(this.id);
    }

    this.connection?.raw.close();
    this.connection = null;
    this.destroy();
  }

  private reaction<T, FireImmediately extends boolean = false>(
    expression: (r: IReactionPublic) => T,
    effect: (
      arg: T,
      prev: FireImmediately extends true ? T | undefined : T,
      r: IReactionPublic,
    ) => void,
    opts?: IReactionOptions<T, FireImmediately>,
  ) {
    this.destructors.push(reaction(expression, effect, opts));
  }

  private setupReactions() {
    this.reaction(() => this.connection, this.handleConnectionChange, {
      fireImmediately: true,
    });
    this.reaction(() => this.connection, this.handleDisconnection);
    this.reaction(() => this.hasConnection, this.handleNetworkReady);
    this.reaction(
      () => [this.active, this.timeWentInactive] as const,
      this.handleInactiveKick,
    );
  }

  private handleConnectionChange = (
    connection: Connection | null,
    prevConnection: Connection | null | undefined,
  ) => {
    if (!prevConnection && connection) {
      this.active = true;
    }

    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (!connection) return;

    this.startHeartbeat(connection);
  };

  private startHeartbeat(connection: Connection) {
    this.heartbeatInterval = setInterval(async () => {
      const messageId = Math.random().toString(36).substring(7);
      connection.send({
        type: NetworkDataType.HEARTBEAT_H2C,
        data: { messageId },
      });

      const didReturn = await this.waitForReturnHeartbeat(connection, messageId, 2_000);
      runInAction(() => {
        if (connection === this.connection) {
          this.updateActiveStatus(didReturn);
        }
      });
    }, 2_500);

    this.destructors.push(() => clearInterval(this.heartbeatInterval!));
  }

  private waitForReturnHeartbeat(
    connection: Connection,
    messageId: string,
    timeCapMs: number,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => {
        listenerDestructor();
        resolve(false);
      }, timeCapMs);

      const listenerDestructor = connection.addDataListener((message) => {
        if (message.data.messageId === messageId) {
          clearTimeout(timeout);
          listenerDestructor();
          resolve(true);
        }
      }, NetworkDataType.HEARTBEAT_RETURN_C2H);
    });
  }

  private updateActiveStatus(didReturn: boolean) {
    if (!this.active && didReturn) this.timeWentInactive = null;
    if (this.active && !didReturn) this.timeWentInactive = Date.now();
    this.active = didReturn;
  }

  private handleDisconnection = () => {
    if (!this.connection && this.active) {
      this.active = false;
      this.timeWentInactive = Date.now();
    }
  };

  private handleNetworkReady = () => {
    if (!this.hasConnection) this.networkReady = false;
  };

  private handleInactiveKick = ([active, timeWentInactive]: readonly [
    boolean,
    number | null,
  ]) => {
    if (active) {
      if (this.kickTimeout) clearTimeout(this.kickTimeout);
      return;
    }

    if (timeWentInactive === null) return;

    const smallBuffer = 250;
    this.kickTimeout = setTimeout(() => {
      if (!this.active && Date.now() - timeWentInactive > INACTIVE_ALLOWANCE) {
        this.kick('inactivity');
      }
    }, INACTIVE_ALLOWANCE + smallBuffer);
  };

  get hasConnection() {
    return !!this.connection;
  }

  get playerInfo(): Omit<PlayerDto, 'ready'> {
    return {
      active: this.active,
      hasConnection: this.hasConnection,
      connectionId: this.id,
      image: this.image,
      isHost: this.isHost,
      screenName: this.screenName,
      subscription: this.subscription,
    };
  }

  get dtoForControllerUserModel(): ControllerUserDto {
    return {
      active: this.active,
      timeWentInactive: this.timeWentInactive,
      hasConnection: this.hasConnection,
      id: this.id,
      image: this.image,
      isHost: this.isHost,
      screenName: this.screenName,
      subscription: this.subscription,
      networkReady: this.networkReady,
    };
  }
}
